import React from 'react';
import { Copy, Trash2, Server, AlertTriangle, Columns3 } from 'lucide-react';
import { DatacenterConfig } from '../../hooks/useDataCenterApi';
import { useEditorStore, AisleType } from '../../store/editorStore';
import { PropertiesEditor } from './PropertiesEditor';
import { TrayInspector } from './TrayInspector';
import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const AISLE_TYPE_OPTIONS: { value: AisleType; label: string; title?: string; varColor: string; varBg: string; varBorder: string }[] = [
  { value: 'cold',        label: 'Холодный коридор', varColor: 'var(--color-cold)', varBg: 'var(--color-cold-bg)', varBorder: 'var(--color-cold-border)' },
  { value: 'hot',         label: 'Горячий коридор',  varColor: 'var(--color-hot)', varBg: 'var(--color-hot-bg)', varBorder: 'var(--color-hot-border)' },
  { value: 'containment', label: 'Containment',      varColor: 'var(--color-containment)', varBg: 'var(--color-containment-bg)', varBorder: 'var(--color-containment-border)' },
];

// ─── Shared KV row ─────────────────────────────────────────────────────────────
function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 11 }}>{label}</span>
      <span style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: 11, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </>
  );
}

// ─── Main sidebar ──────────────────────────────────────────────────────────────
export function PropertiesSidebar({ dc, dcCode }: { dc: DatacenterConfig; dcCode: string }) {
  const {
    selectedIds, racks, aisleZones, telemetryData,
    updateAisleZone, removeAisleZone,
    trayNodes,
    selectedEdgeId, setSelectedEdgeId, trayEdges, updateTrayEdge, deleteTrayEdge,
  } = useEditorStore(useShallow(state => ({
    selectedIds:     state.selectedIds,
    racks:           state.racks,
    aisleZones:      state.aisleZones,
    telemetryData:   state.telemetryData,
    updateAisleZone: state.updateAisleZone,
    removeAisleZone: state.removeAisleZone,
    trayNodes:       state.trayNodes,
    selectedEdgeId:  state.selectedEdgeId,
    setSelectedEdgeId: state.setSelectedEdgeId,
    trayEdges:       state.trayEdges,
    updateTrayEdge:  state.updateTrayEdge,
    deleteTrayEdge:  state.deleteTrayEdge,
  })));

  const { activeWorkspaceId } = useWorkspace();
  const { data: trayOccData } = useQuery({
    queryKey: ['trayOccupancy', dcCode, activeWorkspaceId],
    queryFn: async () => {
      const res = await api.get(`/api/datacenters/${dcCode}/trays/occupancy`, {
        params: { workspace: activeWorkspaceId },
      });
      return res.data;
    },
    refetchInterval: 10000,
  });
  const edgeCounts = trayOccData?.edge_counts || {};

  const selectedEdge = selectedEdgeId ? trayEdges[selectedEdgeId] : null;
  if (selectedEdge) {
    const sourceNode = trayNodes[selectedEdge.source];
    const targetNode = trayNodes[selectedEdge.target];
    const key1 = `${selectedEdge.source}_${selectedEdge.target}`;
    const key2 = `${selectedEdge.target}_${selectedEdge.source}`;
    const cableCount = edgeCounts[key1] || edgeCounts[key2] || selectedEdge.usedCables || 0;

    return (
      <TrayInspector
        edge={selectedEdge}
        sourceNode={sourceNode}
        targetNode={targetNode}
        allEdges={trayEdges}
        allNodes={trayNodes}
        cableCount={cableCount}
        onUpdate={updateTrayEdge}
        onDelete={deleteTrayEdge}
        onClose={() => setSelectedEdgeId(null)}
      />
    );
  }

  const selectedId   = selectedIds.length === 1 ? selectedIds[0] : null;
  const selectedRack = selectedId ? racks[selectedId] : null;
  const selectedZone = selectedId ? aisleZones[selectedId] : null;
  const tele         = selectedRack ? telemetryData[selectedRack.id] : null;

  // ── Multi-select → batch actions ──
  if (selectedIds.length > 1) {
    const selectedRacks = selectedIds.map(id => racks[id]).filter(Boolean);
    const rackCount = selectedRacks.length;
    const zoneCount = selectedIds.length - rackCount;
    return (
      <div style={{ padding: '12px', fontSize: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Групповые действия
        </div>
        
        {/* Count summary badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.1))',
          border: '1px solid var(--color-accent-blue-border, rgba(59, 130, 246, 0.25))',
          borderRadius: 6,
          marginBottom: 14,
          fontSize: 11,
          color: 'var(--color-accent-blue, #60a5fa)',
          fontWeight: 500,
        }}>
          <Server size={13} />
          <span>
            Выделено: <strong style={{ fontFamily: 'var(--font-data)' }}>{rackCount}</strong> стоек
            {zoneCount > 0 && <>, <strong style={{ fontFamily: 'var(--font-data)' }}>{zoneCount}</strong> зон</>}
          </span>
        </div>

        {/* Batch assign row */}
        {rackCount > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Назначить ряд
            </label>
            <select
              style={{
                width: '100%',
                background: 'var(--bg-input, #1f2937)',
                border: '1px solid var(--border-color, #374151)',
                borderRadius: 5,
                padding: '5px 8px',
                fontSize: 12,
                color: 'var(--text-primary, #e2e8f0)',
                cursor: 'pointer',
                outline: 'none',
              }}
              value=""
              onChange={e => {
                const rowId = e.target.value || undefined;
                const itRackIds = selectedRacks.filter(r => !r.isFacility && !r.isBlocked).map(r => r.id);
                if (itRackIds.length) useEditorStore.getState().batchUpdateRacks(itRackIds, { rowId });
              }}
            >
              <option value="">— Выберите ряд назначения —</option>
              {Object.values(useEditorStore.getState().rows).sort((a, b) => a.labelIndex - b.labelIndex).map(row => (
                <option key={row.id} value={row.id}>{row.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Batch power limit */}
        {rackCount > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Лимит мощности для всех (кВт)
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="number"
                  step={0.5}
                  min={0}
                  placeholder="5.0"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--bg-input, #1f2937)',
                    border: '1px solid var(--border-color, #374151)',
                    borderRadius: 5,
                    padding: '5px 8px',
                    fontSize: 12,
                    fontFamily: 'var(--font-data)',
                    color: 'var(--text-primary, #e2e8f0)',
                    outline: 'none',
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const kw = parseFloat((e.target as HTMLInputElement).value);
                      if (!isNaN(kw)) {
                        const itRackIds = selectedRacks.filter(r => !r.isFacility && !r.isBlocked).map(r => r.id);
                        if (itRackIds.length) useEditorStore.getState().batchUpdateRacks(itRackIds, { max_power_w: Math.round(kw * 1000) });
                      }
                    }
                  }}
                />
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', background: 'var(--bg-hover)', padding: '2px 5px', borderRadius: 3, border: '1px solid var(--border, #374151)', fontFamily: 'var(--font-data)' }}>
                ↵ Enter
              </span>
            </div>
          </div>
        )}

        {/* Batch actions buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4, borderTop: '1px solid var(--border, rgba(255,255,255,0.06))' }}>
          <button
            onClick={() => useEditorStore.getState().duplicateSelected()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '6px 10px',
              background: 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.1))',
              border: '1px solid var(--color-accent-blue-border, rgba(59, 130, 246, 0.3))',
              borderRadius: 5,
              color: 'var(--color-accent-blue, #60a5fa)',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.12s ease',
            }}
          >
            <Copy size={12} />
            <span>Дублировать выделенное (Ctrl+D)</span>
          </button>
          <button
            onClick={() => useEditorStore.getState().deleteSelected()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '6px 10px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 5,
              color: 'var(--status-critical, #ef4444)',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.12s ease',
            }}
          >
            <Trash2 size={12} />
            <span>Удалить выделенное (Del)</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Nothing selected → show DC settings (DcInspector) ──
  if (!selectedId) {
    return (
      <div style={{ fontSize: 12 }}>
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            {dc.name}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
            <KV label="Рядов" value={dc.rows_count} />
            <KV label="Стоек в ряду" value={dc.max_racks_per_row} />
            <KV label="Шаг стоек" value={`${dc.rack_step_m} м`} />
            <KV label="Проход" value={`${dc.aisle_width_m} м`} />
            <KV label="Глубина" value={`${dc.rack_depth_m} м`} />
            <KV label="Высота" value={`${dc.rack_height_m} м`} />
          </div>
        </div>
        {/* DcInspector — editor settings, row management, naming masks */}
        <PropertiesEditor selectedId={null} dcCode={dcCode} />
      </div>
    );
  }

  // ── Aisle zone selected ──
  if (selectedZone) {
    const areaM2 = ((selectedZone.w / 100) * (selectedZone.h / 100)).toFixed(1);
    return (
      <div style={{ padding: '12px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
          Коридор
        </div>

        {/* Type selector */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 4 }}>Тип коридора</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {AISLE_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => updateAisleZone(selectedZone.id, { aisleType: opt.value })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 8px', borderRadius: 5, border: '1px solid',
                  cursor: 'pointer', fontSize: 12, textAlign: 'left',
                  background: selectedZone.aisleType === opt.value ? opt.varBg : 'transparent',
                  borderColor: selectedZone.aisleType === opt.value ? opt.varBorder : 'var(--bg-hover)',
                  color: selectedZone.aisleType === opt.value ? opt.varColor : 'var(--text-secondary, #9ca3af)',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: opt.varColor, flexShrink: 0 }} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Label input */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 4 }}>Метка</label>
          <input
            type="text"
            value={selectedZone.label || ''}
            onChange={e => updateAisleZone(selectedZone.id, { label: e.target.value })}
            placeholder="напр. Холодный A"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg-hover)',
              border: '1px solid var(--border-color)',
              borderRadius: 5, padding: '5px 8px',
              fontSize: 12, color: 'var(--text-primary, #e2e8f0)',
            }}
          />
        </div>

        {/* Position & Dimensions (editable) */}
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Позиция и размеры</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>X (м)</label>
            <input type="number" step={0.1}
              value={Math.round(selectedZone.x / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { x: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Y (м)</label>
            <input type="number" step={0.1}
              value={Math.round(selectedZone.y / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { y: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Ширина (м)</label>
            <input type="number" step={0.1} min={0.3}
              value={Math.round(selectedZone.w / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { w: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Глубина (м)</label>
            <input type="number" step={0.1} min={0.3}
              value={Math.round(selectedZone.h / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { h: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 12 }}>
          Площадь: {areaM2} м²
        </div>

        <button
          onClick={() => removeAisleZone(selectedZone.id)}
          style={{
            width: '100%', padding: '6px', borderRadius: 5,
            border: '1px solid var(--status-critical)',
            background: 'rgba(239,68,68,0.08)',
            color: 'var(--status-critical)', fontSize: 12, cursor: 'pointer',
          }}
        >
          Удалить зону
        </button>
      </div>
    );
  }

  // ── Rack / Facility selected ──
  if (selectedRack) {
    const areaM2 = (((selectedRack.widthM || 0.6) * (selectedRack.depthM || 1.0))).toFixed(2);
    const isFac = Boolean(selectedRack.isFacility);
    const isPillar = Boolean(selectedRack.isBlocked || selectedRack.rackType === 'pillar');

    return (
      <div>
        <div style={{ padding: '12px 12px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {isPillar ? 'Колонна' : isFac ? 'Инфраструктура' : 'Стойка'}
        </div>

        {/* Rack editor fields */}
        <PropertiesEditor selectedId={selectedId} dcCode={dcCode} />

        {/* Physical dimensions */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px' }}>
            <KV label="Площадь" value={<span style={{ fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{areaM2} м²</span>} />
            {!isFac && !isPillar && (
              <KV label="U-слоты" value={<span style={{ fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{selectedRack.uCount}</span>} />
            )}
          </div>
        </div>

        {/* Telemetry */}
        {tele && (
          <div style={{ margin: '0 12px 8px', padding: '10px', background: 'var(--color-accent-blue-bg)', border: '1px solid var(--color-accent-blue-border)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>Телеметрия</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', fontSize: 12 }}>
              {tele.temperature !== undefined && (
                <>
                  <span style={{ color: 'var(--text-muted, #6b7280)' }}>Температура</span>
                  <span style={{ color: tele.temperature > 35 ? 'var(--status-critical)' : tele.temperature > 28 ? 'var(--status-warning)' : 'var(--status-normal)', fontWeight: 600, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>
                    {tele.temperature.toFixed(1)} °C
                  </span>
                </>
              )}
              {tele.powerLoad !== undefined && (
                <>
                  <span style={{ color: 'var(--text-muted, #6b7280)' }}>Мощность</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>
                    {tele.powerLoad.toFixed(1)} / {tele.powerCapacity} кВт
                  </span>
                </>
              )}
              {tele.hasSpof && (
                <span style={{ color: 'var(--status-warning)', gridColumn: '1 / -1', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={12} /> SPOF
                </span>
              )}
            </div>
          </div>
        )}

        {selectedIds.length > 1 && (
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color, #1f2937)', marginTop: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginBottom: 6 }}>
              Выделено: {selectedIds.length} объектов
            </div>
            <button
              onClick={() => useEditorStore.getState().setShowRackAllocation(true)}
              style={{
                width: '100%',
                padding: '6px 10px',
                background: 'rgba(168, 85, 247, 0.12)',
                border: '1px solid rgba(168, 85, 247, 0.35)',
                color: '#c084fc',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Columns3 size={13} /> Распределить по группам (M)
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
